<template>
    <BaseDialog v-model:open="isOpen" title="Draft review for Workday" show-close float-close dialog-content-class="md:w-[560px] p-6">
        <div v-if="draft" class="flex flex-col gap-5">
            <div class="flex flex-col gap-1">
                <h3 class="title-section text-valence-grey-800">Your review</h3>
                <p class="body-small text-valence-grey-600">Drafted from your conversation. Review or edit each comment, then submit it to Workday.</p>
            </div>

            <template v-if="!submitted">
                <section v-for="section in SECTION_DEFS" :key="section.field" class="flex flex-col gap-1">
                    <h4 class="body-semibold text-valence-grey-800">{{ section.title }}</h4>
                    <BaseTextarea
                        v-model:input="localDraft[section.field]"
                        rows="3"
                        :disabled="loading"
                        :data-testid="`edit-${section.field}`"
                        :aria-label="section.title"
                        :placeholder="`Add a comment for ${section.title}.`"
                        class="caret-valence-primary-500 leading-[normal]" />
                </section>

                <div v-if="noticeMessage" class="body-small text-red-700" role="alert">{{ noticeMessage }}</div>

                <div class="flex justify-end gap-2">
                    <BaseButton theme="tertiary" :disabled="loading" @click="isOpen = false">Cancel</BaseButton>
                    <BaseButton theme="primary" data-testid="submit-self-review-dialog" :disabled="loading" class="flex items-center justify-center gap-1" @click="handleSubmit">
                        <LoadingSpinner v-if="loading" class="h-4" />Submit to Workday
                    </BaseButton>
                </div>
            </template>

            <div v-else class="flex flex-col items-center gap-3 py-4 text-center" data-testid="self-review-submitted">
                <i class="bi bi-check-circle text-2xl text-valence-primary-500" aria-hidden="true"></i>
                <p class="nadia-chat text-valence-grey-800">Submitted to your Workday review.</p>
                <BaseButton theme="primary" @click="isOpen = false">Done</BaseButton>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { inject, reactive, ref, watch } from "vue";

interface SelfReviewDraft {
    goals_comment: string;
    values_comment: string;
    performance_comment: string;
    overall_comment: string;
}
type CommentField = keyof SelfReviewDraft;

const SECTION_DEFS: Array<{ field: CommentField; title: string }> = [
    { field: "goals_comment", title: "Goals" },
    { field: "values_comment", title: "Values & Competencies" },
    { field: "performance_comment", title: "Performance" },
    { field: "overall_comment", title: "Overall Summary" },
];

interface Props {
    draft: SelfReviewDraft | null;
    messageId: number;
}

const props = defineProps<Props>();
const isOpen = defineModel<boolean>("open", { default: false });

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const submitted = ref(false);
const noticeMessage = ref("");

function cloneDraft(source: SelfReviewDraft | null): SelfReviewDraft {
    return {
        goals_comment: source?.goals_comment ?? "",
        values_comment: source?.values_comment ?? "",
        performance_comment: source?.performance_comment ?? "",
        overall_comment: source?.overall_comment ?? "",
    };
}

const localDraft = reactive<SelfReviewDraft>(cloneDraft(props.draft));

// Reset the editable copy and state whenever a fresh draft is opened.
watch(
    () => [props.draft, isOpen.value] as const,
    ([draft, open]) => {
        if (open && draft) {
            Object.assign(localDraft, cloneDraft(draft));
            submitted.value = false;
            noticeMessage.value = "";
        }
    },
);

async function handleSubmit() {
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}submit_self_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await execute(ChatAction.SUBMIT_SELF_REVIEW_DRAFT, { message_id: props.messageId, draft: { ...localDraft } }, { sync: true });
        if (result?.success) {
            submitted.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't submit your review to Workday. Please try again later.";
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't submit your review to Workday. Please try again later.";
    } finally {
        stopLoading();
    }
}
</script>
